import * as SelectPrimitive from '@radix-ui/react-select';
import * as Flags from 'country-flag-icons/react/3x2';
import { Check, ChevronDown, Globe, Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { COUNTRY_DIAL_CODES, DEFAULT_DIAL_ISO2 } from '../../lib/countryDialCodes';
import { cn } from '../../lib/utils';

import { FieldChrome } from './FieldChrome';

/** Windows doesn't render Unicode regional-indicator flag emoji as flags (it
 * shows the bare two-letter code instead), so real flag SVGs are used here
 * rather than emoji — same flag set `react-phone-number-input` defaults to. */
type FlagComponent = (props: { className?: string }) => JSX.Element;

interface DialCode {
  dial: string;
  label: string;
  Flag: FlagComponent | undefined;
}

const FLAGS = Flags as unknown as Record<string, FlagComponent>;

// Every country `country-flag-icons` has a flag for (~195), Sri Lanka
// pinned first (Sanath Gamage Coaching's client base is overwhelmingly Sri
// Lankan), the rest alphabetical by label. "Other" lets a coach paste a
// full `+<code>...` number verbatim for anything not listed.
const DEFAULT_DIAL = COUNTRY_DIAL_CODES.find((c) => c.iso2 === DEFAULT_DIAL_ISO2)!.dial;
const DIAL_CODES: DialCode[] = [
  COUNTRY_DIAL_CODES.find((c) => c.iso2 === DEFAULT_DIAL_ISO2)!,
  ...COUNTRY_DIAL_CODES.filter((c) => c.iso2 !== DEFAULT_DIAL_ISO2).sort((a, b) =>
    a.label.localeCompare(b.label),
  ),
].map((c) => ({ dial: c.dial, label: c.label, Flag: FLAGS[c.iso2] }));
/** Longest-dial-code-first so a 3-digit code like `971` isn't shadowed by a
 * shorter one that happens to be a prefix of it. */
const DIAL_CODES_BY_LENGTH = [...DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);
// Radix forbids an empty-string Item value, so "Other" (dial === '') is
// remapped to this sentinel at the Radix boundary only.
const OTHER_SENTINEL = '__brainkey_phone_other__';

function findDial(dial: string): DialCode | undefined {
  return DIAL_CODES.find((c) => c.dial === dial);
}

function parsePhone(value: string): { dial: string; national: string } {
  const trimmed = value.trim();
  if (trimmed === '') return { dial: DEFAULT_DIAL, national: '' };
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    const match = DIAL_CODES_BY_LENGTH.find((c) => digits.startsWith(c.dial));
    if (match) return { dial: match.dial, national: trimmed.slice(1 + match.dial.length).trim() };
    return { dial: '', national: trimmed };
  }
  // No leading '+' — a bare local number typed without a country code
  // (the common case for a coach used to dialing locally); assume the
  // default country rather than treating it as unparseable.
  return { dial: DEFAULT_DIAL, national: trimmed };
}

function composePhone(dial: string, national: string): string {
  const trimmedNational = national.trim();
  if (trimmedNational === '') return '';
  return dial === '' ? trimmedNational : `+${dial} ${trimmedNational}`;
}

function FlagBadge({ Flag }: { Flag?: FlagComponent | undefined }): JSX.Element {
  return (
    <span className="flex h-4 w-[22px] shrink-0 items-center justify-center overflow-hidden rounded-[3px] ring-1 ring-black/5 dark:ring-white/10">
      {Flag ? (
        <Flag className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center bg-zinc-200 dark:bg-zinc-700">
          <Globe className="h-2.5 w-2.5 text-zinc-500 dark:text-zinc-400" />
        </span>
      )}
    </span>
  );
}

const itemClasses = cn(
  'relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer select-none outline-none transition-colors duration-150',
  'hover:bg-brand-50 dark:hover:bg-white/5 data-[highlighted]:bg-brand-50 dark:data-[highlighted]:bg-white/5',
);

export interface PhoneNumberFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  optional?: boolean;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
}

/** A single merged phone field — a flag + dial-code picker fused to the left
 * edge of the number input (defaults to Sri Lanka), rather than two separate
 * boxes side by side. The dial code is kept as independent local state
 * (rather than re-derived from `value` every render) so picking a country
 * before typing any digits isn't immediately discarded — `composePhone`
 * returns '' while the national number is still empty (so the field reads
 * as genuinely empty for "optional" checks upstream), which would otherwise
 * round-trip back through `parsePhone('')` and snap the picker back to the
 * default. `normalizePhone` (called by callers at submit time) still does
 * the final format cleanup.
 *
 * With ~195 countries listed, a text filter sits above the country list so
 * a coach can type ahead (e.g. "sing" -> Singapore) instead of scrolling. */
export function PhoneNumberField({
  label = 'Phone number',
  hint,
  optional,
  value,
  onChange,
  id,
  disabled,
}: PhoneNumberFieldProps): JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const [dial, setDial] = useState(() => parsePhone(value).dial);
  const [filter, setFilter] = useState('');
  const { national } = parsePhone(value);
  const current = findDial(dial);

  const filteredCodes = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q === '') return DIAL_CODES;
    return DIAL_CODES.filter((c) => c.label.toLowerCase().includes(q) || c.dial.includes(q));
  }, [filter]);

  return (
    <FieldChrome label={label} hint={hint} optional={optional} id={fieldId}>
      <div
        className={cn(
          'flex items-stretch rounded-2xl border border-zinc-200 bg-zinc-50 transition-all duration-300 focus-within:ring-2 focus-within:ring-zinc-900/10 dark:border-white/10 dark:bg-zinc-900/80 dark:focus-within:ring-brand-500/20',
          disabled && 'opacity-50',
        )}
      >
        <SelectPrimitive.Root
          disabled={disabled ?? false}
          value={dial === '' ? OTHER_SENTINEL : dial}
          onValueChange={(next) => {
            const nextDial = next === OTHER_SENTINEL ? '' : next;
            setDial(nextDial);
            onChange(composePhone(nextDial, national));
          }}
          onOpenChange={(open) => {
            if (!open) setFilter('');
          }}
        >
          <SelectPrimitive.Trigger
            type="button"
            aria-label="Country code"
            className="flex shrink-0 items-center gap-1.5 rounded-l-2xl py-2.5 pl-3 pr-2 text-sm text-zinc-900 outline-none dark:text-zinc-100 disabled:cursor-not-allowed"
          >
            <FlagBadge Flag={current?.Flag} />
            <SelectPrimitive.Value>{dial === '' ? 'Other' : `+${dial}`}</SelectPrimitive.Value>
            <SelectPrimitive.Icon>
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              sideOffset={6}
              className="z-[1100] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-soft dark:border-white/10 dark:bg-zinc-900 dark:shadow-glass-dark"
            >
              <div className="flex items-center gap-2 border-b border-zinc-200 px-3 py-2 dark:border-white/10">
                <Search className="h-3.5 w-3.5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                <input
                  autoFocus
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  // Radix's own type-ahead would otherwise steal these
                  // keystrokes to jump-select an Item; only Escape (to close
                  // the popover) and Enter/arrow keys are let through.
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') e.stopPropagation();
                  }}
                  placeholder="Search country or code…"
                  className="w-full bg-transparent text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-zinc-100 dark:placeholder:text-zinc-500"
                />
              </div>
              <SelectPrimitive.Viewport className="max-h-72 p-1.5">
                <SelectPrimitive.Item value={OTHER_SENTINEL} className={itemClasses}>
                  <FlagBadge />
                  <SelectPrimitive.ItemText>Other (type full number)</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="ml-auto">
                    <Check className="w-3.5 h-3.5 text-brand-500" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
                {filteredCodes.length === 0 && (
                  <p className="px-3 py-2 text-xs text-zinc-400 dark:text-zinc-500">
                    No countries match “{filter}”.
                  </p>
                )}
                {filteredCodes.map((c) => (
                  <SelectPrimitive.Item
                    key={c.dial + c.label}
                    value={c.dial}
                    className={itemClasses}
                  >
                    <FlagBadge Flag={c.Flag} />
                    <SelectPrimitive.ItemText>{c.label}</SelectPrimitive.ItemText>
                    <span className="ml-auto flex items-center gap-2 text-xs text-zinc-400 dark:text-zinc-500">
                      +{c.dial}
                      <SelectPrimitive.ItemIndicator>
                        <Check className="w-3.5 h-3.5 text-brand-500" />
                      </SelectPrimitive.ItemIndicator>
                    </span>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        <div className="my-2 w-px bg-zinc-200 dark:bg-white/10" />

        <input
          id={fieldId}
          type="tel"
          placeholder="77 123 4567"
          value={national}
          onChange={(e) => onChange(composePhone(dial, e.target.value))}
          disabled={disabled}
          className="min-w-0 flex-1 rounded-r-2xl bg-transparent px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-zinc-100 dark:placeholder:text-zinc-500 disabled:cursor-not-allowed"
        />
      </div>
    </FieldChrome>
  );
}
